import React, { useMemo, useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import Card from "./Card";
import { SocEmptyFrame } from "./SocChartCard";
import SocCveDetails, { ExploitBadges } from "./SocCveDetails";
import { useAllRiskDetails } from "../hooks/useAllRiskDetails";
import { SEVERITY } from "./socChartTheme";
import { formatAge, formatLocal, formatLocalDate, parseUtc } from "./socFreshness";
import { FOCUS_RING, SeverityBadge, buttonClass } from "./socUi";
import {
  CVSS_HIGH,
  DEFAULT_SORT,
  COLLECTION_LABEL,
  FIRST_DETECTED_TIP,
  collectionLine,
  collectionPaused,
  coverageLine,
  financialLabel,
  fromSnapshot,
  hasFigures,
  registerLabel,
  snapshotCoverageLine,
  defenderSync,
  filterCves,
  formatCvss,
  isPartialCoverage,
  pausedLine,
  publishedText,
  severityKey,
  sortCves,
  usableCoverage,
} from "./socVulnerabilities";

const NOT_CONNECTED = "Defender XDR isn't connected, so no CVEs are read.";
const NONE_OPEN = "No open vulnerabilities on the devices Defender rates medium or high for risk or exposure.";
// While collection is paused an empty list means nothing was collected before the pause, not "none open".
const NONE_COLLECTED_PAUSED = "No CVEs were collected before collection was paused.";
const NONE_IN_SNAPSHOT = "No open vulnerabilities in Defender's latest snapshot.";
// Rows per page: a tenant can hold well over a thousand open CVEs.
export const CVE_PAGE_SIZE = 25;

// The three states, each labelled on its own (a published snapshot never implies risks or figures caught up).
const StateChips = ({ g }) => (
  <div className="mt-2 flex flex-wrap gap-2 text-xs" data-testid="soc-cves-states">
    {[
      ["collection", "CVE collection", COLLECTION_LABEL[g.collectionState] || g.collectionState],
      ["register", "Risk register", registerLabel(g.riskRegisterState)],
      ["financial", "Financial figures", financialLabel(g.financialState)],
    ].map(([key, label, value]) => (
      <span key={key} data-testid={`soc-cves-state-${key}`}
        className="rounded-full border border-border px-2.5 py-0.5 text-content-secondary">
        {`${label}: `}<span className="font-medium text-content-primary">{value}</span>
      </span>
    ))}
  </div>
);

// Click a column: highest first; click again: lowest first; a third time: back to the default order.
const nextSort = (sort, key) => {
  if (sort.key !== key) return { key, dir: "desc" };
  if (sort.dir === "desc") return { key, dir: "asc" };
  return DEFAULT_SORT;
};

const SortHeader = ({ label, column, sort, onSort, className = "" }) => {
  const active = sort.key === column;
  const ariaSort = active ? (sort.dir === "asc" ? "ascending" : "descending") : "none";
  return (
    <th aria-sort={ariaSort} className={`py-1.5 pr-3 font-medium ${className}`}>
      <button
        type="button"
        onClick={() => onSort(column)}
        data-testid={`soc-cves-sort-${column}`}
        className={`inline-flex items-center gap-1 rounded hover:text-content-primary ${FOCUS_RING} ${active ? "text-content-primary" : ""}`}
      >
        {label}
        {active && (sort.dir === "asc"
          ? <ArrowUp className="h-3 w-3" aria-hidden="true" />
          : <ArrowDown className="h-3 w-3" aria-hidden="true" />)}
      </button>
    </th>
  );
};

// The ways a CVE is counted, side by side so they are never confused.
const Figures = ({ v }) => (
  <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="soc-cves-figures">
    {[
      ["uniqueCves", "Open CVEs", v.uniqueCves],
      ["instances", "CVE instances on devices", v.instances],
      ["exposedDevices", "Devices with critical/high CVEs", v.exposedDevices],
      ["withKnownExploit", "CVEs with a known exploit", v.withKnownExploit ?? 0],
    ].map(([key, label, value]) => (
      <div key={key} className="rounded-lg bg-theme-secondary px-3 py-2">
        <dt className="text-xs text-content-muted">{label}</dt>
        <dd className="text-xl font-semibold tabular-nums text-content-primary" data-testid={`soc-cves-${key}`}>{value}</dd>
      </div>
    ))}
  </dl>
);

const FilterChip = ({ on, onToggle, testId, children }) => (
  <button type="button" aria-pressed={on} onClick={onToggle} data-testid={testId}
    className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${FOCUS_RING} ${
      on ? "border-accent-cyan bg-accent-cyan-muted text-accent-cyan" : "border-border text-content-secondary hover:bg-theme-hover"
    }`}>
    {children}
  </button>
);

// A CVE's details open under its row (SocCveDetails), the way All Risks expands a risk.
const CveTable = ({ cves, openCve, onToggle, now, allRisks, asOfRunId, snapshot }) => {
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [exploitOnly, setExploitOnly] = useState(false);
  const [cvssHigh, setCvssHigh] = useState(false);
  const [page, setPage] = useState(1);
  const sorted = useMemo(
    () => sortCves(filterCves(cves, { exploitOnly, cvssHigh }), sort),
    [cves, sort, exploitOnly, cvssHigh]);
  // Sorting and filtering cover the whole list; a new order or filter starts again at page 1.
  const onSort = (key) => { setSort((s) => nextSort(s, key)); setPage(1); };
  const onFilter = (set) => { set((x) => !x); setPage(1); };
  const pages = Math.max(1, Math.ceil(sorted.length / CVE_PAGE_SIZE));
  const current = Math.min(page, pages); // a refresh can shrink the list under the page shown
  const shown = sorted.slice((current - 1) * CVE_PAGE_SIZE, current * CVE_PAGE_SIZE);

  return (
    <>
      <div className="mb-2 flex flex-wrap items-center gap-2" role="group" aria-label="Filter CVEs">
        <FilterChip on={exploitOnly} onToggle={() => onFilter(setExploitOnly)} testId="soc-cves-filter-exploit">
          Known exploit only
        </FilterChip>
        <FilterChip on={cvssHigh} onToggle={() => onFilter(setCvssHigh)} testId="soc-cves-filter-cvss">
          {`CVSS ${CVSS_HIGH.toFixed(1)} or higher`}
        </FilterChip>
        {(exploitOnly || cvssHigh) && (
          <span className="text-xs text-content-muted" data-testid="soc-cves-filter-count">
            {`Showing ${sorted.length} of ${cves.length}`}
          </span>
        )}
      </div>
      {/* Like the incident queue: every CVE in a box that scrolls on its own under a sticky header,
          so a long list never makes the card taller. */}
      <div className="max-h-[32rem] overflow-auto rounded-lg border border-border" data-testid="soc-cves-scroll">
        <table className="w-full text-left text-sm" data-testid="soc-cves-table">
          <caption className="sr-only">
            Open CVEs. Default order: highest severity, then a known exploit, then highest CVSS, then most affected
            devices, then oldest first detected.
          </caption>
          <thead className="sticky top-0 z-10 bg-theme-card-solid shadow-[0_1px_0_var(--border-color)]">
            <tr className="text-xs text-content-muted">
              <th className="py-1.5 pr-3 font-medium">CVE ID</th>
              <SortHeader label="Severity" column="severity" sort={sort} onSort={onSort} />
              <SortHeader label="CVSS" column="cvss" sort={sort} onSort={onSort} />
              <th className="py-1.5 pr-3 font-medium">Exploit</th>
              <SortHeader label="Affected devices" column="devices" sort={sort} onSort={onSort} />
              <SortHeader label="First detected" column="firstDetected" sort={sort} onSort={onSort} />
              <SortHeader label="Published" column="published" sort={sort} onSort={onSort} />
              <th className="py-1.5 font-medium"><span className="sr-only">Action</span></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((c) => {
              const isOpen = openCve === c.cveId;
              return (
              <React.Fragment key={c.cveId}>
              <tr className={`border-t border-border ${isOpen ? "bg-accent-cyan-muted" : ""}`} data-testid="soc-cves-row">
                <td className="py-2 pr-3 font-medium">
                  {/* Same toggle as the "View devices" button, so the CVE ID itself opens its devices. */}
                  <button type="button" className="text-left text-accent-cyan hover:underline" onClick={() => onToggle(c.cveId)}
                    aria-expanded={isOpen} data-testid="soc-cves-id-toggle">
                    {c.cveId}
                  </button>
                </td>
                <td className="py-2 pr-3"><SeverityBadge severity={severityKey(c.severity)} /></td>
                <td className={`py-2 pr-3 tabular-nums ${c.details?.cvssScore == null ? "text-xs text-content-muted" : "text-content-primary"}`}>
                  {formatCvss(c.details?.cvssScore)}
                </td>
                <td className="py-2 pr-3"><ExploitBadges details={c.details} /></td>
                <td className="py-2 pr-3 tabular-nums text-content-primary">{c.deviceCount}</td>
                <td className="py-2 pr-3 text-content-secondary" title={snapshot ? FIRST_DETECTED_TIP.snapshot : FIRST_DETECTED_TIP.legacy}>
                  {formatLocalDate(parseUtc(c.firstDetected)) || "Not available"}
                </td>
                <td className="py-2 pr-3 text-xs text-content-secondary">{publishedText(c.details?.publishedOn, now)}</td>
                <td className="py-2">
                  <button type="button" className={buttonClass("sm")} onClick={() => onToggle(c.cveId)}
                    aria-expanded={isOpen} aria-label={`View devices affected by ${c.cveId}`}>
                    {isOpen ? "Hide devices" : "View devices"}
                  </button>
                </td>
              </tr>
              {isOpen && (
                <tr className="border-t border-border bg-theme-secondary" data-testid={`soc-cves-details-row-${c.cveId}`}>
                  <td colSpan={8} className="px-3 py-3">
                    <SocCveDetails cveId={c.cveId} now={now} allRisks={allRisks} asOfRunId={asOfRunId} />
                  </td>
                </tr>
              )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {sorted.length === 0 && (
        <p className="py-3 text-sm text-content-muted" data-testid="soc-cves-filter-empty">No open CVE matches these filters.</p>
      )}
      {pages > 1 && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-content-muted" data-testid="soc-cves-pager">
          <span data-testid="soc-cves-page-count">{`${sorted.length} CVEs · page ${current} of ${pages}`}</span>
          <button type="button" className={buttonClass("sm")} disabled={current <= 1} onClick={() => setPage(current - 1)}>Previous</button>
          <button type="button" className={buttonClass("sm")} disabled={current >= pages} onClick={() => setPage(current + 1)}>Next</button>
        </div>
      )}
    </>
  );
};

// The open CVE list (/soc Overview and Endpoints view). `vulnerabilities` is null when Defender XDR
// isn't connected; then the card keeps its place and says so, never showing zeros.
// `riskDetailsKey` (the queue's changeKey) re-reads the full risks after a write or Refresh.
export const SocTopVulnerabilities = ({ vulnerabilities: v, endpointTile, now = new Date(), riskDetailsKey }) => {
  const [openCve, setOpenCve] = useState(null);
  const allRisks = useAllRiskDetails({ resetKey: riskDetailsKey });

  if (!v) {
    return (
      <div data-testid="soc-cves">
        <Card className="h-full">
          <h3 className="font-semibold text-content-primary">Open vulnerabilities</h3>
          <SocEmptyFrame testId="soc-cves-empty">{NOT_CONNECTED}</SocEmptyFrame>
        </Card>
      </div>
    );
  }

  const snapshot = fromSnapshot(v);
  const g = v.generation;
  if (snapshot && !hasFigures(v)) {
    // Nothing published yet: the state, never a zero.
    return (
      <div data-testid="soc-cves">
        <Card className="h-full">
          <h3 className="font-semibold text-content-primary">Open vulnerabilities</h3>
          <StateChips g={g} />
          <SocEmptyFrame testId="soc-cves-starting">{collectionLine(g)}</SocEmptyFrame>
        </Card>
      </div>
    );
  }

  const sync = defenderSync(endpointTile, now);
  const coverage = usableCoverage(v.coverage, v.instances, sync.lastSync);
  const partial = !snapshot && isPartialCoverage(coverage);
  const paused = snapshot ? g.collectionState === "Paused" : collectionPaused(v.coverage);

  return (
    <div data-testid="soc-cves">
      <Card className="h-full">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-semibold text-content-primary">Open vulnerabilities</h3>
            <p className="text-xs text-content-muted">Defender XDR CVEs, one row per CVE</p>
            {snapshot && <StateChips g={g} />}
          </div>
          {snapshot && (
            <p className={`text-xs ${g.collectionState === "Current" ? "text-content-muted" : "text-status-warning"}`} data-testid="soc-cves-snapshot">
              {collectionLine(g)}
            </p>
          )}
          {!snapshot && sync.lastSync && (
            <p className={`text-xs ${sync.stale ? "text-status-warning" : "text-content-muted"}`} data-testid="soc-cves-sync"
              title={formatLocal(sync.lastSync)}>
              {sync.stale
                ? `Defender XDR last finished a sync ${formatAge(sync.ageMs)} ago; these figures may be out of date`
                : `Defender XDR last finished a sync ${formatAge(sync.ageMs)} ago`}
            </p>
          )}
        </div>

        <SocCveSeverityStrip vulnerabilities={v} />

        <div className="mt-3"><Figures v={v} /></div>

        {!snapshot && paused && (
          <p data-testid="soc-cves-paused" className="mt-3 rounded-md bg-status-warning-bg px-3 py-2 text-xs text-content-primary">
            {pausedLine(v.coverage)}
          </p>
        )}
        <p data-testid="soc-cves-coverage"
          className={`mt-3 rounded-md px-3 py-2 text-xs ${partial ? "bg-status-warning-bg text-content-primary" : "bg-theme-secondary text-content-secondary"}`}>
          {snapshot ? snapshotCoverageLine(v.coverage, g) : coverageLine(coverage)}
        </p>
        {snapshot && v.devicesWithAnyCve != null && (
          <p data-testid="soc-cves-any-device" className="mt-2 text-xs text-content-secondary">
            {`Devices with any open CVE: ${v.devicesWithAnyCve}`}
          </p>
        )}
        {v.unparsed > 0 && (
          <p data-testid="soc-cves-unparsed" className="mt-2 rounded-md bg-status-warning-bg px-3 py-2 text-xs text-content-primary">
            {`${v.unparsed} vulnerability ${v.unparsed === 1 ? "record" : "records"} couldn't be grouped by CVE. `}
            {`${v.unparsed === 1 ? "It is" : "They are"} counted in CVE instances only, not in open CVEs, severity or devices.`}
          </p>
        )}
        {v.withoutDetails > 0 && (
          <p data-testid="soc-cves-without-details" className="mt-2 rounded-md bg-theme-secondary px-3 py-2 text-xs text-content-secondary">
            {`CVSS and exploit details aren't available yet for ${v.withoutDetails} ${v.withoutDetails === 1 ? "CVE" : "CVEs"}.`}
            {!paused && (snapshot ? " They fill in when Microsoft publishes them for these CVEs." : " They fill in when Defender XDR next reads a device that has them.")}
          </p>
        )}

        <div className="mt-3">
          {v.cves.length === 0
            ? <SocEmptyFrame testId="soc-cves-none">{snapshot ? NONE_IN_SNAPSHOT : paused ? NONE_COLLECTED_PAUSED : NONE_OPEN}</SocEmptyFrame>
            : <CveTable cves={v.cves} openCve={openCve} now={now} allRisks={allRisks} asOfRunId={g?.asOfRunId} snapshot={snapshot}
                onToggle={(id) => setOpenCve((cur) => (cur === id ? null : id))} />}
        </div>
      </Card>
    </div>
  );
};

// Low to Critical, left to right, as the main dashboard's Vulnerability Distribution strip.
const STRIP = [
  ["low", "Low"],
  ["medium", "Medium"],
  ["high", "High"],
  ["critical", "Critical"],
];

// Unique CVEs per severity (never device instances) as one proportional strip, in the style of the main
// dashboard's Vulnerability Distribution: each count above its slice, the total at the end. Low CVEs
// aren't collected by the sync, so Low appears only when the summary has one (a rating a re-sync
// replaces) and otherwise says "Low: not collected"; an Unrated slice appears only when there is one.
export const SocCveSeverityStrip = ({ vulnerabilities: v }) => {
  const counts = v?.bySeverity || {};
  const segments = [...STRIP, ...(counts.unknown > 0 ? [["unknown", "Unrated"]] : [])]
    .map(([key, label]) => ({ key, label, count: Number(counts[key]) || 0 }))
    .filter((s) => s.count > 0);
  const total = segments.reduce((sum, s) => sum + s.count, 0);
  if (!v || total === 0) return null;

  return (
    <div className="mt-3 flex items-center gap-4 rounded-lg border border-border px-4 py-3" data-testid="soc-chart-cve-severity"
      role="group" aria-label="Unique CVEs by severity">
      <div className="whitespace-nowrap text-[11px] font-semibold uppercase leading-tight tracking-widest text-content-muted">
        Unique CVEs<br />by severity
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex">
          {segments.map(({ key, label, count }) => (
            <div key={key} style={{ width: `${(count / total) * 100}%` }} className="flex min-w-0 flex-col items-center overflow-hidden px-0.5"
              data-testid={`soc-cve-severity-${key}`}>
              <span className="text-xs font-extrabold leading-none tabular-nums" style={{ color: SEVERITY[key] || "var(--text-muted)" }}>{count}</span>
              <span className="mt-0.5 w-full truncate text-center text-[10px] leading-none text-content-muted">{label}</span>
            </div>
          ))}
        </div>
        <div className="flex h-3 overflow-hidden rounded-full bg-theme-secondary" aria-hidden="true">
          {segments.map(({ key, count }) => (
            <div key={key} style={{ width: `${(count / total) * 100}%`, background: SEVERITY[key] || "var(--text-muted)" }} />
          ))}
        </div>
        {!(counts.low > 0) && (
          <p className="mt-1 text-[10px] text-content-muted" data-testid="soc-cve-severity-low-note">Low: not collected</p>
        )}
      </div>
      <div className="flex-shrink-0 border-l border-border pl-4 text-center">
        <div className="text-lg font-extrabold leading-none tabular-nums text-content-primary">{total}</div>
        <div className="mt-0.5 text-xs text-content-muted">Unique CVEs</div>
      </div>
    </div>
  );
};

// The CVE card, its severity strip at the top, full width; the same on the Overview and in the Endpoints view.
const SocVulnerabilitiesSection = ({ vulnerabilities, endpointTile, now, riskDetailsKey }) => (
  <section aria-label="Vulnerabilities" className="mb-4 min-w-0">
    <SocTopVulnerabilities vulnerabilities={vulnerabilities} endpointTile={endpointTile} now={now} riskDetailsKey={riskDetailsKey} />
  </section>
);

export default SocVulnerabilitiesSection;
